import type { TaxRate } from 'store/returns/models/returnData';

export type TaxNameOption = { label: string; value: string };

export type TaxDisplayRowFields = {
  taxRateId?: string | null;
  taxName?: string | null;
  taxRate?: number | null;
};

/** Format tax rate for display in brackets, e.g. `20%` (matches tax rate column). */
export function formatTaxRateBracket(rate: number | null | undefined): string {
  if (rate == null || Number.isNaN(Number(rate))) return '';
  return `${rate}%`;
}

/** Tax name with rate in brackets, e.g. `Standard Rated (20%)`. */
export function formatTaxNameDisplayLabel(
  name: string,
  rate?: number | null
): string {
  const trimmedName = String(name ?? '').trim();
  if (!trimmedName) return '';
  const rateBracket = formatTaxRateBracket(rate);
  return rateBracket ? `${trimmedName} (${rateBracket})` : trimmedName;
}

/** Strip a trailing ` (rate%)` suffix when matching plain stored tax names. */
export function taxOptionBaseName(label: string): string {
  const trimmed = String(label ?? '').trim();
  const match = trimmed.match(/^(.+?)\s+\([^)]+%\)\s*$/);
  return match ? match[1].trim() : trimmed;
}

export function buildTaxNameByIdMap(taxRates: TaxRate[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const rate of taxRates) {
    const id = String(rate?.id ?? '').trim();
    const name = String(rate?.name ?? '').trim();
    if (!id || !name) continue;
    const label = formatTaxNameDisplayLabel(name, rate.taxRate);
    map.set(id, label);
    map.set(id.toLowerCase(), label);
  }
  return map;
}

export function buildTaxRateByIdMap(taxRates: TaxRate[]): Map<string, TaxRate> {
  const map = new Map<string, TaxRate>();
  for (const rate of taxRates) {
    const id = String(rate?.id ?? '').trim();
    if (!id) continue;
    map.set(id, rate);
    map.set(id.toLowerCase(), rate);
  }
  return map;
}

/** Dropdown options: label = `name (rate%)`, value = tax rate id. */
export function buildTaxNameOptions(taxRates: TaxRate[]): TaxNameOption[] {
  return [...taxRates]
    .map((rate) => {
      const name = String(rate?.name ?? '').trim();
      const value = String(rate?.id ?? '').trim();
      return {
        label: formatTaxNameDisplayLabel(name, rate.taxRate),
        value,
      };
    })
    .filter((o) => o.label && o.value)
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Dropdown options: label = `name (rate%)`, value = plain tax name (for taxName fields). */
export function buildTaxNameValueOptions(taxRates: TaxRate[]): TaxNameOption[] {
  const seen = new Set<string>();
  return [...taxRates]
    .map((rate) => {
      const name = String(rate?.name ?? '').trim();
      if (!name || seen.has(name.toLowerCase())) return null;
      seen.add(name.toLowerCase());
      return {
        label: formatTaxNameDisplayLabel(name, rate.taxRate),
        value: rate.id,
      };
    })
    .filter((o): o is TaxNameOption => o != null)
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function lookupTaxNameInOptions(
  key: string,
  map: Map<string, string>,
  options: TaxNameOption[]
): string | undefined {
  const trimmed = key.trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();
  return (
    map.get(trimmed) ??
    map.get(lower) ??
    options.find((o) => String(o.value) === trimmed)?.label ??
    options.find((o) => String(o.value).toLowerCase() === lower)?.label ??
    options.find((o) => o.label === trimmed)?.label ??
    options.find((o) => taxOptionBaseName(o.label) === trimmed)?.label
  );
}

/** Resolve Tax Name column text from taxRateId + organisation tax rates. */
export function resolveTaxDisplayLabel(
  row: TaxDisplayRowFields | undefined,
  cellVal: unknown,
  taxNameByIdMap: Map<string, string>,
  taxNameOptions: TaxNameOption[]
): string {
  const taxRateId =
    row?.taxRateId != null && String(row.taxRateId).trim() !== ''
      ? String(row.taxRateId).trim()
      : '';
  const storedTaxName =
    row?.taxName != null && String(row.taxName).trim() !== ''
      ? String(row.taxName).trim()
      : '';
  const rowTaxRate = row?.taxRate;
  const fromCell =
    cellVal != null && String(cellVal).trim() !== ''
      ? String(cellVal).trim()
      : '';

  if (taxRateId) {
    const resolved = lookupTaxNameInOptions(
      taxRateId,
      taxNameByIdMap,
      taxNameOptions
    );
    if (resolved) return resolved;
  }

  if (storedTaxName) {
    const byName = lookupTaxNameInOptions(
      storedTaxName,
      taxNameByIdMap,
      taxNameOptions
    );
    if (byName) return byName;
    if (storedTaxName !== taxRateId) {
      return formatTaxNameDisplayLabel(storedTaxName, rowTaxRate);
    }
  }

  if (fromCell && fromCell !== taxRateId) {
    const resolved = lookupTaxNameInOptions(
      fromCell,
      taxNameByIdMap,
      taxNameOptions
    );
    if (resolved) return resolved;
    return formatTaxNameDisplayLabel(fromCell, rowTaxRate);
  }

  return '-';
}

const matchesTaxOption = (o: TaxNameOption, trimmed: string, lower: string) =>
  String(o.value) === trimmed ||
  String(o.value).toLowerCase() === lower ||
  o.label === trimmed ||
  taxOptionBaseName(o.label) === trimmed;

export function findTaxOptionInList(
  needle: string | undefined,
  options: TaxNameOption[]
): TaxNameOption | undefined {
  if (!needle?.trim()) return undefined;
  const trimmed = needle.trim();
  const lower = trimmed.toLowerCase();
  return options.find((o) => matchesTaxOption(o, trimmed, lower));
}

export function taxOptionExistsInList(
  value: string,
  options: TaxNameOption[]
): boolean {
  return !!findTaxOptionInList(value, options);
}

export function buildTaxSelectOptions(
  options: TaxNameOption[],
  fallback?: TaxNameOption | null
): TaxNameOption[] {
  if (fallback && !taxOptionExistsInList(fallback.value, options)) {
    return [fallback, ...options];
  }
  return options;
}

export function resolveTaxRawValue(
  row: TaxDisplayRowFields | undefined,
  draftRow: TaxDisplayRowFields | undefined
): string | undefined {
  const id =
    (draftRow?.taxRateId != null ? String(draftRow.taxRateId).trim() : '') ||
    (row?.taxRateId != null ? String(row.taxRateId).trim() : '');
  return id || undefined;
}

/** Value + label + options for inline Tax Name Select (never show raw id in the field). */
export function resolveTaxSelectEditState(params: {
  row: TaxDisplayRowFields | undefined;
  draftRow: TaxDisplayRowFields | undefined;
  cellVal: unknown;
  taxNameByIdMap: Map<string, string>;
  taxNameOptions: TaxNameOption[];
}): {
  value: string | undefined;
  label: string | undefined;
  options: TaxNameOption[];
} {
  const { row, draftRow, cellVal, taxNameByIdMap, taxNameOptions } = params;

  const displayLabelRaw = resolveTaxDisplayLabel(
    { ...row, ...draftRow },
    cellVal,
    taxNameByIdMap,
    taxNameOptions
  );
  const displayLabel = displayLabelRaw === '-' ? undefined : displayLabelRaw;

  const rawVal = resolveTaxRawValue(row, draftRow);

  const matched =
    (rawVal ? findTaxOptionInList(rawVal, taxNameOptions) : undefined) ??
    (displayLabel
      ? findTaxOptionInList(displayLabel, taxNameOptions)
      : undefined);

  const value = matched?.value ?? rawVal;
  const label = matched?.label ?? displayLabel ?? value;

  const fallback =
    value && label
      ? taxOptionExistsInList(value, taxNameOptions)
        ? null
        : { label, value }
      : null;

  const options = buildTaxSelectOptions(taxNameOptions, fallback);

  return { value, label, options };
}

export function syncTaxFieldsFromRateId<T extends TaxDisplayRowFields>(
  body: T,
  taxRateByIdMap: Map<string, TaxRate>
): T {
  const id = body.taxRateId != null ? String(body.taxRateId).trim() : '';
  if (!id) return body;
  const rate = taxRateByIdMap.get(id) ?? taxRateByIdMap.get(id.toLowerCase());
  if (!rate) return body;
  return {
    ...body,
    taxName: rate.name,
    taxRate: rate.taxRate,
  };
}
